import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

// src/pages/TicketRecall/types.ts
// Kiểu dữ liệu cho phân hệ "Tạo ticket thu hồi vé ế" (REQUIRE_FIXBUGS.md mục 13.3) — "vé ế" là vé
// ĐÃ GIAO cho đại lý nhưng bán không hết, đại lý báo lại số lượng/dải số cụ thể về công ty (qua
// điện thoại/giấy tờ, nhân viên nội bộ nhập hộ vào hệ thống — cùng mô hình với AgentConfirm mục
// 13.2), công ty không tự biết đại lý nào còn ế bao nhiêu nên PHẢI chọn đại lý khi lập phiếu (không
// còn "kho tổng" như thiết kế ban đầu) — xem thêm ConfirmTicketRecallCommand.cs phía backend.

// Trạng thái phiếu thu hồi — khớp TicketRecallStatuses bên backend: Draft=1, Confirmed=2,
// Cancelled=3, Approved=4, Rejected=5. Mục 23, REQUIRE_FIXBUGS.md — Approved/Rejected là bước
// "Duyệt"/"Từ chối" SAU khi đã Confirmed, lưu trực tiếp trên TicketRecall (không tách bảng riêng).
export const TICKET_RECALL_STATUS = {
  Draft: 1,
  Confirmed: 2,
  Cancelled: 3,
  Approved: 4,
  Rejected: 5,
} as const;

export type TicketRecallStatus = 1 | 2 | 3 | 4 | 5;

// Mục 8.d, README_UPDATE.md — đổi nhãn 1/2 cho đúng mô tả nghiệp vụ (đại lý tạo đề xuất → chờ thẩm
// định), không đổi giá trị số/state machine đang chạy đúng.
export const TICKET_RECALL_STATUS_LABELS: Record<string, string> = {
  1: st('de_xuat_thu_hoi_text'),
  2: st('cho_tham_dinh_text'),
  3: st('da_huy_text'),
  4: st('da_duyet_text'),
  5: st('tu_choi_text'),
};

export const TICKET_RECALL_STATUS_COLORS: Record<string, string> = {
  1: 'warning',
  2: 'info',
  3: 'danger',
  4: 'success',
  5: 'danger',
};

export const TICKET_RECALL_STATUS_OPTIONS = [
  { value: 'All', label: st('tat_ca_trang_thai_label') },
  { value: '1', label: st('de_xuat_thu_hoi_text') },
  { value: '2', label: st('cho_tham_dinh_text') },
  { value: '3', label: st('da_huy_text') },
  { value: '4', label: st('da_duyet_text') },
  { value: '5', label: st('tu_choi_text') },
];

export interface TicketRecallLine {
  id?: number;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity?: number;
}

// Mục 11.b, README_UPDATE.md — chi tiết 1 phiếu giao gốc mà phiếu thu hồi đang xem "ăn" theo.
export interface TicketRecallSourceDelivery {
  deliveryCode: string;
  deliveredAt?: string | null;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity: number;
}

export interface TicketRecall {
  id: number;
  recallCode: string;
  issuancePlanId: number;
  planCode?: string | null;
  // null/undefined = thu hồi từ kho tổng; có giá trị = thu hồi từ tồn kho của đại lý này (FE tự
  // resolve tên đại lý từ agentId, giống TicketDelivery).
  agentId?: number | null;
  status: TicketRecallStatus | number;
  statusName?: string | null;
  statusBadgeColor?: string | null;
  note?: string | null;
  totalQuantity?: number | null;
  confirmedBy?: number | null;
  confirmedByName?: string | null;
  confirmedAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
  createdAt?: string | null;
  // Mục 23, REQUIRE_FIXBUGS.md — người/thời điểm Duyệt hoặc Từ chối (Status=Approved/Rejected),
  // lưu trực tiếp trên TicketRecall. Null = chưa Duyệt/Từ chối.
  appraisedBy?: number | null;
  appraisedByName?: string | null;
  appraisedAt?: string | null;
  appraisalComment?: string | null;
  // Mục 8.f, README_UPDATE.md — vị trí kho lưu trữ (chỉ gán được khi Status = Approved).
  warehouseLocation?: string | null;
  warehouseAssignedBy?: number | null;
  warehouseAssignedByName?: string | null;
  warehouseAssignedAt?: string | null;
  // Best-effort mã các phiếu giao gốc mà phiếu thu hồi này "ăn" theo — rỗng nếu thu hồi từ kho
  // tổng hoặc không tìm được dữ liệu khớp.
  sourceDeliveryCodes?: string[];
  // Mục 11.b, README_UPDATE.md — bản chi tiết hơn sourceDeliveryCodes (kỳ/ký hiệu/dải/tổng
  // giao/thời điểm giao của từng phiếu giao gốc), dùng cho modal đối chiếu giao → thu hồi.
  sourceDeliveries?: TicketRecallSourceDelivery[];
  // Mục 11.b — tổng vé đã giao/đã thu hồi/đã bán được của CẢ đại lý trong CÙNG kế hoạch (không chỉ
  // riêng phiếu đang xem) + công nợ ước tính = đã bán × đơn giá bình quân. Undefined nếu thu hồi từ
  // kho tổng (không có cơ sở đại lý để so sánh).
  agentTotalDeliveredQuantity?: number | null;
  agentTotalRecalledQuantity?: number | null;
  agentTotalSoldQuantity?: number | null;
  agentDebtValue?: number | null;
  // Mục 11.c, README_UPDATE.md — quy đổi tổng vé thu hồi (của CHÍNH phiếu này) ra thành tiền (đơn
  // giá bình quân của phiếu giao gốc cùng kỳ + đại lý, fallback giá vé mặc định nếu thiếu dữ liệu).
  estimatedValue?: number | null;
  // Bổ sung — SNAPSHOT % hoa hồng bán hàng riêng của đại lý, đóng băng lúc lập phiếu thu hồi. Dùng
  // để đảo chính xác phần HH bán đã trả cho số vé ế này. Null = phiếu cũ / không đọc được system-service.
  salesCommissionRate?: number | null;
  lines?: TicketRecallLine[];
}

export const TICKET_RECALL_APPRAISAL_COMMENT_MAX_LENGTH = 500;

// Body POST /ticket-api/TicketRecall — agentId bắt buộc (xem comment đầu file).
export interface CreateTicketRecallPayload {
  issuancePlanId: number;
  agentId: number;
  note?: string;
  lines: Array<{ symbol: string; rangeStart: string; rangeEnd: string }>;
}

export interface TicketRecallPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}
